import type { SiteConfig } from "@/config/schema";

let initialised = false;

/**
 * Sentry is plug-and-play and inert by default.
 *
 * DSN resolution order:
 *   1. `integrations.sentry.dsn` in the runtime/bundled site JSON
 *   2. `VITE_SENTRY_DSN` build-time env var
 *
 * A Sentry DSN is a publishable client key by design — it is safe in the
 * browser bundle. Never put a Sentry AUTH TOKEN (used for source-map upload
 * or the Sentry API) in this project: keep it in CI secrets only.
 * Without a DSN, Sentry never loads and makes no network requests.
 */
export async function initSentry(config: SiteConfig) {
  if (initialised || typeof window === "undefined") return;

  const settings = config.integrations?.sentry;
  if (!settings?.enabled) return;

  const dsn = settings.dsn || process.env.SENTRY_DSN || "";
  if (!dsn) return;

  initialised = true;
  const Sentry = await import("@sentry/react");
  Sentry.init({
    dsn,
    environment: settings.environment || process.env.NODE_ENV,
    tracesSampleRate: settings.tracesSampleRate ?? 0,
    replaysSessionSampleRate: settings.replaysSessionSampleRate ?? 0,
    // Do not ship user PII to the error tracker.
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.request) {
        delete event.request.cookies;
        delete event.request.data;
        if (event.request.headers) {
          delete event.request.headers["Authorization"];
          delete event.request.headers["Cookie"];
        }
      }
      return event;
    },
  });
}
